import { ActionTree } from 'vuex'
import State, { ExportFileRequest, Model, SectionTable } from './types'
import { Catalog, Category, ManufacturingSeries, Product, ProductClass, RootState } from '../types'
import { Api } from '@/api/Api'
import { isCustomerAdminSite } from '@/helpers/sites.helper'
import SecurityState from '../security/types'
import { MaterialGroup } from '../search/types'

const api = new Api()

export const actions: ActionTree<State, RootState> = {
  // TODO: simplify and move to a common place!
  setCatalogId({ commit }, { catalogId }: { catalogId?: number }): any {
    commit('setCatalogId', { catalogId })
  },
  getCatalogs({ commit }): any {
    return api.fetch({
      url: '/site/AllCatalogs',
    }).then(response => {
      const catalogs: Catalog[] = response.List
      return catalogs
    }).catch(() => {
      commit('error')
    })
  },
  getTopology({ commit, state, rootState }): any {
    const securityState = (this.state as any).security as SecurityState
    return api.fetchV2({
      url: `${rootState.apiPrefix ?? ''}/modelfinder/VisibleCategories?catalogId=${state.CatalogId ?? ''}`,
      user: securityState.currentUser,
    })
      .then(response => {
        const categories: Category[] = response.List
        categories.forEach((category: Category) => {
          category.open = false
          category.Download = true
          category.ProductClasses.forEach((productClass: ProductClass) => {
            productClass.open = false
            productClass.Download = true
            productClass.ManufacturingSeries.forEach((manufacturingSeries: ManufacturingSeries) => {
              manufacturingSeries.open = false
              manufacturingSeries.Download = true
              manufacturingSeries.Products.forEach((product: Product) => {
                product.Download = true
              })
            })
          })
        })
        commit('success')
        return categories
      }).catch(() => {
        commit('error')
      })
  },
  getModel({ commit }, { catalogNumber }: { catalogNumber: string }): any {
    return api.fetch({
      url: '/modelviewer/model/' + catalogNumber,
    })
      .then(response => {
        const ret: Model = response.Data
        commit('success')
        return ret
      }).catch(() => {
        commit('error')
      })
  },
  getCategory(x, { categoryId }: { categoryId: number }): any {
    return new Promise(resolve => { resolve({ CategoryId: categoryId }) })
  },
  getProductClass(x, { productClassId }: { productClassId: number }): any {
    return new Promise(resolve => { resolve({ ProductClassId: productClassId }) })
  },
  getManufacturingSeriesWithModels({ commit, state }, { manufacturingSeriesId }: { manufacturingSeriesId: number }): any {
    return api.fetch({
      url: '/modelviewer/manufacturingSeries/' + manufacturingSeriesId + `?catalogId=${state.CatalogId ?? ''}`,
    })
      .then(response => {
        const ret: ManufacturingSeries = response.Data
        commit('success')
        return ret
      }).catch(() => {
        commit('error')
      })
  },
  getProductWithModels({ commit, state }, { productId }: { productId: number }): any {
    return api.fetch({
      url: '/modelviewer/product/' + productId + `?catalogId=${state.CatalogId ?? ''}`,
    })
      .then(response => {
        const ret: Product = response.Data
        ret.Sections.forEach((section) => {
          section.Download = true
          section.Tabs.forEach((tab) => {
            tab.Download = true
            tab.Tables.forEach((table) => {
              table.Download = true
              table.Models.forEach((m) => {
                m.Download = true
              })
            })
          })
        })
        commit('success')
        return ret
      }).catch(() => {
        commit('error')
      })
  },
  getModelsBySeries({ commit, state }, { seriesNumber }: { seriesNumber: string }): any {
    return api.fetch({
      url: '/modelviewer/series/' + seriesNumber + `?catalogId=${state.CatalogId ?? ''}`,
    })
      .then(response => {
        const ret = response.List
        ret[0].Download = true
        ret[0].Products.forEach((product) => {
          product.Download = true
          product.Sections.forEach((section) => {
            section.Download = true
            section.Tabs.forEach((tab) => {
              tab.Download = true
              tab.Tables.forEach((table) => {
                table.Download = true
                table.Models.forEach((m) => {
                  m.Download = true
                })
              })
            })
          })
        })

        commit('success')
        return ret
      }).catch(() => {
        commit('error')
      })
  },
  getSectionTable({ commit, state }, { sectionTableId }: { sectionTableId: number }): any {
    return api.fetchV2({
      url: '/modelviewer/sectionTable/' + sectionTableId + `?catalogId=${state.CatalogId ?? ''}`,
    })
      .then(response => {
        const sectionTable: SectionTable = response.Data
        commit('success')
        return sectionTable
      }).catch(() => {
        commit('error')
      })
  },
  getExportFile({ state, rootState }, request: ExportFileRequest): any {
    let url = `${rootState.apiPrefix ?? ''}/export/`
    if (request.categoryId) {
      url += `category/?categoryid=${encodeURIComponent(request.categoryId)}&ShowPricing=${encodeURIComponent(request.showPricing)}&ShowInventory=${encodeURIComponent(request.showInventory)}`
    } else if (request.productClassId) {
      url += `productclass/?productclassid=${encodeURIComponent(request.productClassId)}&ShowPricing=${encodeURIComponent(request.showPricing)}&ShowInventory=${encodeURIComponent(request.showInventory)}`
    } else if (request.manufacturingSeriesId) {
      url += `manufacturingseries/?manufacturingSeriesId=${encodeURIComponent(request.manufacturingSeriesId)}&ShowPricing=${encodeURIComponent(request.showPricing)}&ShowInventory=${encodeURIComponent(request.showInventory)}`
    } else if (request.pcnCatalogNumber) {
      url += `model/?pcncatalognumber=${encodeURIComponent(request.pcnCatalogNumber)}&ShowPricing=${encodeURIComponent(request.showPricing)}&ShowInventory=${encodeURIComponent(request.showInventory)}`
    } else if (request.sectionTableId) {
      url += `sectiontable/?sectionTableId=${encodeURIComponent(request.sectionTableId)}&ShowPricing=${encodeURIComponent(request.showPricing)}&ShowInventory=${encodeURIComponent(request.showInventory)}`
    } else if (request.sectionTabId) {
      url += `sectiontab/?sectionTabId=${encodeURIComponent(request.sectionTabId)}&ShowPricing=${encodeURIComponent(request.showPricing)}&ShowInventory=${encodeURIComponent(request.showInventory)}`
    } else if (request.productId) {
      url += `product/?productId=${encodeURIComponent(request.productId)}&ShowPricing=${encodeURIComponent(request.showPricing)}&ShowInventory=${encodeURIComponent(request.showInventory)}`
    } else if (request.materialGroupNumber) {
      url += `materialGroup/?materialGroupNumber=${encodeURIComponent(request.materialGroupNumber)}&ShowPricing=${encodeURIComponent(request.showPricing)}&ShowInventory=${encodeURIComponent(request.showInventory)}`
    } else if (request.all) {
      url += `all/?ShowPricing=${encodeURIComponent(request.showPricing)}&ShowInventory=${encodeURIComponent(request.showInventory)}`
    } else if (request.customerNumber) {
      url += `customerMaterial/?ShowPricing=${encodeURIComponent(request.showPricing)}&ShowInventory=${encodeURIComponent(request.showInventory)}`
    }
    url += `&siteName=${rootState.site.Name}`
    if (request.customerNumber) {
      url += '&customerNumber=' + encodeURIComponent(request.customerNumber)
    }
    if (state.CatalogId) {
      url += '&catalogId=' + encodeURIComponent(state.CatalogId)
    }
    if (isCustomerAdminSite(rootState.role)) {
      return window.open(`/api${url}`, '_blank')
    }
    const securityState = (this.state as any).security as SecurityState
    return api.fetchV2({
      url,
      user: securityState.currentUser,
    }).then(response => {
      return window.open(`/api/export/download/${response.key}`, '_blank')
    })
  },
  getModelsByMaterialGroup({ commit }, { materialGroup }: { materialGroup: MaterialGroup }): any {
    return api.fetch({
      url: '/modelviewer/materialGroup/' + encodeURIComponent(materialGroup.MaterialGroupNumber ?? ''),
    })
      .then(response => {
        response.List.forEach((m) => {
          m.Download = true
        })
        const models: Model[] = response.List
        commit('success')
        return models
      }).catch(() => {
        commit('error')
      })
  },
  getCustomExportFile({ state, rootState }, request: ExportFileRequest): any {
    const securityState = (this.state as any).security as SecurityState
    let obj = {}
    let url = `${rootState.apiPrefix ?? ''}/export/`
    if (request.categoryId) {
      url += `customCategory/?CategoryId=${encodeURIComponent(request.categoryId)}&ShowPricing=${encodeURIComponent(request.showPricing)}&ShowInventory=${encodeURIComponent(request.showInventory)}`
      obj = request.data.map(d => ({
        ProductClassId: d.ProductClassId,
        ManufacturingSeries: d.ManufacturingSeries.map(m => ({
          ManufacturingSeriesId: m.ManufacturingSeriesId,
          Products: m.Products.map(p => ({
            ProductId: p.ProductId,
          })),
        })),
      }))
    } else if (request.productClassId) {
      url += `customProductclass/?productclassid=${encodeURIComponent(request.productClassId)}&ShowPricing=${encodeURIComponent(request.showPricing)}&ShowInventory=${encodeURIComponent(request.showInventory)}`
      obj = request.data.map(m => ({
        ManufacturingSeriesId: m.ManufacturingSeriesId,
        Products: m.Products.map(p => ({
          ProductId: p.ProductId,
        })),
      }))
    } else if (request.manufacturingSeriesId) {
      url += `customManufacturingseries/?manufacturingSeriesId=${encodeURIComponent(request.manufacturingSeriesId)}&ShowPricing=${encodeURIComponent(request.showPricing)}&ShowInventory=${encodeURIComponent(request.showInventory)}`
      obj = request.data.map(p => ({
        ProductId: p.ProductId,
      }))
    } else if (request.productId) {
      url += `customProduct/?ProductId=${encodeURIComponent(request.productId)}&ShowPricing=${encodeURIComponent(request.showPricing)}&ShowInventory=${encodeURIComponent(request.showInventory)}`
      obj = request.data.map(s => ({
        SectionId: s.SectionId,
        Tabs: s.Tabs.map(t => ({
          SectionTabId: t.SectionTabId,
          Tables: t.Tables.map(table => ({
            SectionTableId: table.SectionTableId,
            Models: table.Models.map(m => ({
              PcnCatalogNumber: m.PcnCatalogNumber,
            })),
          })),
        })),
      }))
    } else if (request.seriesId) {
      url += `customManufacturingseries/?manufacturingSeriesId=${encodeURIComponent(request.data[0].ManufacturingSeriesId)}&ShowPricing=${encodeURIComponent(request.showPricing)}&ShowInventory=${encodeURIComponent(request.showInventory)}`
      obj = request.data[0].Products.map(p => ({
        ProductId: p.ProductId,
        Sections: p.Sections.map(s => ({
          SectionId: s.SectionId,
          Tabs: s.Tabs.map(t => ({
            SectionTabId: t.SectionTabId,
            Tables: t.Tables.map(table => ({
              SectionTableId: table.SectionTableId,
              Models: table.Models.map(m => ({
                PcnCatalogNumber: m.PcnCatalogNumber,
              })),
            })),
          })),
        })),
      }))
    } else if (request.data.length > 0 && !request.all) {
      url += `customMaterialGroup/?ShowPricing=${encodeURIComponent(request.showPricing)}&ShowInventory=${encodeURIComponent(request.showInventory)}`
      obj = request.data.map(m => ({
        PcnCatalogNumber: m.PcnCatalogNumber,
      }))
    } else if (request.all) {
      url += `customAllCategories/?ShowPricing=${encodeURIComponent(request.showPricing)}&ShowInventory=${encodeURIComponent(request.showInventory)}`
      obj = request.data.map(c => ({
        CategoryId: c.CategoryId,
        ProductClasses: c.ProductClasses.map(pc => ({
          ProductClassId: pc.ProductClassId,
          ManufacturingSeries: pc.ManufacturingSeries.map(m => ({
            ManufacturingSeriesId: m.ManufacturingSeriesId,
            Products: m.Products.map(p => ({
              ProductId: p.ProductId,
            })),
          })),
        })),
      }))
    }

    url += `&siteName=${rootState.site.Name}`
    if (request.customerNumber) {
      url += '&customerNumber=' + encodeURIComponent(request.customerNumber)
    }
    if (state.CatalogId) {
      url += '&catalogId=' + encodeURIComponent(state.CatalogId)
    }

    return api.fetchV2({
      url,
      user: securityState.currentUser,
      obj: obj,
      method: 'POST',
    }).then(response => {
      return window.open(`/api/export/download/${response.key}`, '_blank')
    })
  },
}
